import { useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { cn, ia } from 'lib/helpers/utility';
import { useTags } from 'lib/hooks/queries/tags/useTags';
import useDisplaySettings from 'lib/hooks/useDisplaySettings';
import { useTagContext } from 'lib/context/TagContext/TagContext';

import Checkbox from 'components/shared/Checkbox/Checkbox';
import { userState as user_state } from 'components/state';
import ErrorBoundary from 'components/shared/Error/Boundary';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import TagsView from 'components/shared/Tags/components/TagsView/TagsView';
import TagCreate from 'components/shared/Tags/components/TagCreate/TagCreate';

import { labelledTriggerProps } from '../lib/labelledTriggerProps';

import TagsDropdownPanel from './TagsDropdownPanel/TagsDropdownPanel';

const TagTableView = (props) => {
  const {
    currTags = [],
    handleAdd = () => {},
    handleRemove = () => {},
    tagsToAdd = [],
    setTagsToAdd = () => {},
    tagsKind = null,
    selectActions = [],
    tableActions = [],
    isCreateModal = false,
    setIsCreateModal = () => {},
    usePendingState = false,
    savingTagIds = [],
    fromTable = false,
    fromPatientChart = false,
    fromFaxes = false,
    popoverPosition = 'left',
    popoverPanelClassName,
    readOnly = false,
    showTags = true,
    triggerLabel,
    removeConfirmationTitle = "Are you sure you want to remove this tag from the patient's chart?"
  } = props;

  const userState = useRecoilValue(user_state);
  const { saveDisplaySettings } = useDisplaySettings(userState);
  const { data } = useTags({
    params: { kind: tagsKind },
    dependencies: [tagsKind],
    options: { enabled: !readOnly }
  });
  const tags = data?.tags || [];
  const { importantTags, regularTags, updateTags, updateTagsForTables } = useTagContext() || {};
  const tagSettings = userState?.display_settings?.patientChartTags;
  const [selectedTags, setSelectedTags] = useState(currTags || []);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [isRemoveConfirmation, setIsRemoveConfirmation] = useState(
    !tagSettings?.showConfirmations?.remove
  );
  const [pendingTags, setPendingTags] = useState([]);
  const [tagToRemove, setTagToRemove] = useState();

  const scrollPositionRef = useRef(0); // to store scroll position
  const panelRef = useRef(null);

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [scrollPositionRef.current]);

  const handleScroll = () => {
    if (panelRef.current) {
      scrollPositionRef.current = panelRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (fromTable && !fromFaxes) {
      const safeTags = ia(currTags) ? currTags : [];
      setSelectedTags(safeTags.map((tagLink) => tagLink?.tag).filter(Boolean));
      updateTagsForTables(safeTags);
    }
    if (fromPatientChart) {
      updateTags(currTags, tags);
    }
    // Drop the local add-buffer once server truth refreshes — otherwise a
    // later "add" replays every tag ever picked this session, even ones the
    // user has since removed.
    if (usePendingState) setPendingTags([]);
  }, [currTags]);

  const updateDisplaySettings = () => {
    saveDisplaySettings({
      ...userState.display_settings,
      patientChartTags: {
        ...userState.display_settings?.patientChartTags,
        showConfirmations: {
          ...userState.display_settings?.patientChartTags?.showConfirmations,
          remove: !isRemoveConfirmation
        }
      }
    });
    handleRemove && handleRemove(tagToRemove);
    setSelectedTags(selectedTags.filter((selectedTag) => selectedTag.id !== tagToRemove));
    setShowRemoveConfirmation(false);
  };

  const handleUpdateTags = (tag) => {
    if (selectedTags.some((selectedTag) => selectedTag.id === tag.id)) {
      if (!isRemoveConfirmation) {
        setShowRemoveConfirmation(true);
        setTagToRemove(tag?.id);
      } else {
        setSelectedTags(selectedTags.filter((selectedTag) => selectedTag.id !== tag.id));
        handleRemove && handleRemove(tag?.id);
      }
    } else {
      setSelectedTags([...selectedTags, tag]);
      addTag(tag);
    }
  };

  const addTag = (tag) => {
    if (usePendingState) {
      const newTags = [...pendingTags, tag];
      setPendingTags(newTags);
      handleAdd(newTags);
    } else {
      const newTags = [...tagsToAdd, tag];
      setTagsToAdd(newTags);
      handleAdd(newTags);
    }
  };

  const handleCloseRemoveTagModal = () => setShowRemoveConfirmation(false);
  return (
    <ErrorBoundary>
      <div className="flex h-full items-center justify-end gap-2">
        {showTags && (
          <TagsView
            currTags={currTags}
            itemsToShow={2}
            label={false}
            handleRemove={handleUpdateTags}
            appendTo={document.body}
            fromTable={fromTable}
            fromPatientChart={fromPatientChart}
            importantTags={importantTags}
            regularTags={regularTags}
            fromFaxes={fromFaxes}
            disableDeviceCheck={fromTable}
            kind={tagsKind}
            readOnly={readOnly}
          />
        )}
        {!readOnly && (
          <Popover
            isFixed
            position={popoverPosition}
            icon="tag-new-mono"
            iconSize={20}
            panelClassName={cn('!p-0 z-[99]', popoverPanelClassName)}
            isBorder={false}
            {...labelledTriggerProps(triggerLabel)}>
            {({ closePopover }) => (
              <TagsDropdownPanel
                savingTagIds={savingTagIds}
                tags={tags}
                selectedTags={selectedTags}
                actions={fromTable ? tableActions : selectActions}
                handleUpdateTags={handleUpdateTags}
                closePopover={closePopover}
                panelRef={panelRef}
                onScroll={handleScroll}
                scrollPositionRef={scrollPositionRef}
                tagsKind={tagsKind}
              />
            )}
          </Popover>
        )}

        <Confirm
          isOpen={!!showRemoveConfirmation}
          variant="danger"
          title={removeConfirmationTitle}
          icon="pin-filled"
          iconColor="danger"
          iconSize={24}
          handleClose={handleCloseRemoveTagModal}
          footerActions={{
            primary: {
              text: 'Remove',
              onClick: updateDisplaySettings,
              color: 'danger'
            },
            secondary: { text: 'No, not at this time' }
          }}>
          <Checkbox
            isChecked={isRemoveConfirmation}
            handleClick={() => setIsRemoveConfirmation(!isRemoveConfirmation)}
            label="Don't show this message again"
            labelClassName="text-sm !text-neutral-600"
            size={18}
          />
        </Confirm>
        <TagCreate
          isOpen={!!isCreateModal}
          handleClose={() => setIsCreateModal(false)}
          kind={tagsKind}
          addNewTagFromCreated={handleUpdateTags}
        />
      </div>
    </ErrorBoundary>
  );
};

export default TagTableView;
